import {
  addEnglishTestScore,
  deleteEnglishTestScore,
  updateEnglishTestScore
} from '@crm/services.api';
import {
  errorNotificationHandler,
  successNotificationHandler
} from '@moxie/shared';
import {
  IAddEnglishTestScore,
  IUpdateEnglishTestScore
} from '@shared-components/models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { crmErrorHandler } from 'libs/shared/src/crm-modules/shared';
import { AxiosError } from 'axios';
import { LANGUAGE_SCORE_RESPONSE } from '@moxie/constants';
import { useAppSelector } from '@crm/core';
import { RootState } from '@crm/src/core/store';

export const useToeflMutation = () => {
  const queryClient = useQueryClient();

  const socket = useAppSelector((state) => state.socket.wss);
  const user = useAppSelector((state) => state.auth.user);
  const contactData = useAppSelector(
    (state: RootState) => state?.contact?.singleData
  );

  return useMutation({
    mutationFn: (body: IAddEnglishTestScore) => addEnglishTestScore({ ...body, parentId: contactData.id }),
    onSuccess: ({ data }) => {
      successNotificationHandler(LANGUAGE_SCORE_RESPONSE.UPDATE_SUCCESS);
      queryClient.invalidateQueries({
        queryKey: ['englishTestScore'],
      });
      socket?.emit('server::profile-updated', {
        activitiesTypeId: contactData.id,
        activitiesType: 'language-test',
        activitiesAction: 'created',
        userId: user?.id,
        contactId: contactData?.id,
        companyId: user?.companyId as string,
        data: { testName: 'TOEFL' },
      });
    },
    onError: () => {
      errorNotificationHandler('Error adding TOEFL score.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['toefl'] });
    },
  });
};

export const useToeflUpdate = () => {
  const queryClient = useQueryClient();

  const socket = useAppSelector((state) => state.socket.wss);
  const user = useAppSelector((state) => state.auth.user);
  const contactData = useAppSelector(
    (state: RootState) => state?.contact?.singleData
  );

  return useMutation({
    mutationFn: (body: IUpdateEnglishTestScore) => updateEnglishTestScore({ ...body, contactId: contactData.id }),
    onSuccess: async ({ data }) => {
      successNotificationHandler(LANGUAGE_SCORE_RESPONSE.UPDATE_SUCCESS);
      queryClient.invalidateQueries({
        queryKey: ['englishTestScore'],
      });
      socket?.emit('server::profile-updated', {
        activitiesTypeId: contactData.id,
        activitiesType: 'language-test',
        activitiesAction: 'updated',
        userId: user?.id,
        contactId: contactData?.id,
        companyId: user?.companyId as string,
        data: { testName: 'TOEFL' },
      });
    },
    onError: () => {
      errorNotificationHandler('Error updating TOEFL score.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['updateToefl'] });
    },
  });
};

export const useToeflDelete = () => {
  const socket = useAppSelector((state) => state.socket.wss);
  const user = useAppSelector((state) => state.auth.user);
  const contactData = useAppSelector(
    (state: RootState) => state?.contact?.singleData
  );

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteEnglishTestScore(id),
    onSuccess: async ({ data }) => {
      successNotificationHandler(LANGUAGE_SCORE_RESPONSE.DELETE_SUCCESS);
      queryClient.invalidateQueries({
        queryKey: ['englishTestScore'],
      });

      socket?.emit('server::profile-updated', {
        activitiesTypeId: contactData.id,
        activitiesType: 'language-test',
        activitiesAction: 'deleted',
        userId: user?.id,
        contactId: contactData?.id,
        companyId: user?.companyId as string,
        data: { testName: 'TOEFL' },
      });
    },
    onError: (error: AxiosError) => {
      crmErrorHandler(error);
      errorNotificationHandler('Error deleting TOEFL score.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deleteToefl'] });
    },
  });
};
